import React, { useMemo } from 'react'
import AdxSortTable from 'components/reusable/table/AdxSortTable'
import Box from '@mui/material/Box'
import { Skeleton } from '@mui/material'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import { SUBMITTEDREPORT } from '../../data/reportData'
import StatusBar from 'components/reusable/statusBar/AdxStatusBar'
import { useLocation, useNavigate } from 'react-router-dom'
import { getQueryReports } from 'services/api/modules/claim/report'
import { useQuery } from 'react-query'
import { ReportIconConstructor } from '../../functions'
import reportStatusConverter from 'module/claim/services/helper/reportStatusConverter'
import capitalizeFirstWord from 'services/helper/capitalizeFirstWord'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import { Clear } from '@mui/icons-material'

const WaitingApprovalReportTable = () => {
  const unsubmittedReportColumn = [
    {
      Header: 'Claim reports',
      accessor: 'claimReport',
      Cell: (props) => {
        const { claim_report_type, title, start_date, end_date } = props.row.original
        return isLoading ? (
          <Box display="flex" alignItems="center" height="50px">
            <Skeleton variant="text" width={300} />
          </Box>
        ) : (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            columnGap={3}
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={50}
                height={50}
                bgcolor="alternate.light"
                borderRadius={'50%'}
              >
                <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>
                  {ReportIconConstructor(claim_report_type)}
                </Icon>
              </Box>
              <Box>
                <Typography
                  width={{
                    xs: 'calc(100vw - 400px)',
                    lg: 'calc(100vw - 1200px)',
                  }}
                  minWidth={{ xs: 1, lg: '200px' }}
                  noWrap
                  variant="body1"
                  color="dark-text.main "
                >
                  {capitalizeFirstWord(title)}
                </Typography>
                <Box display={'flex'} alignItems={'center'} columnGap={1.5}>
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {claim_report_type}
                  </Typography>
                  <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle_rounded</Icon>
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {start_date} - {end_date}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Expense amount',
      accessor: 'totalAmount',
      Cell: (props) => {
        const { amount_total, expenses } = props.row.original
        return isLoading ? (
          <Box display="flex" alignItems="center" height="50px">
            <Skeleton variant="text" width={200} />
          </Box>
        ) : (
          <Box display={'flex'} flexDirection="column" justifyContent="center" rowGap={0.5}>
            <Typography
              align="start"
              variant="body1"
              color="light-gray-text.main"
              fontWeight="bold"
            >
              RM {parseFloat(amount_total).toFixed(2)}
            </Typography>
            {/* <Typography
              align="center"
              variant="body2"
              color="light-gray-text.main"
            >
              ({expenses?.length}{" "}
              {expenses?.length > 1 ? "expenses" : "expense"})
            </Typography> */}
          </Box>
        )
      },
    },
    {
      Header: 'Reimbursement',
      accessor: 'reimbursedAmount',
      Cell: (props) => {
        const { amount_payable, report_status } = props.row.original

        return isLoading ? (
          <Box display="flex" alignItems="center" height="50px">
            <Skeleton variant="text" width={200} />
          </Box>
        ) : (
          <Box display={'flex'} justifyContent="start" alignItems={'center'} columnGap={2}>
            <StatusBar status={reportStatusConverter(report_status?.name)} />

            <Typography
              noWrap
              variant="body1"
              color="dark-text.main"
              fontWeight="bold"
              component="span"
              mr={3}
            >
              RM {parseFloat(amount_payable).toFixed(2)}
            </Typography>
          </Box>
        )
      },
    },
  ]

  // API CALL
  const { isLoading, data } = useQuery('pendingApproverReportsData', async () => {
    const { data } = await getQueryReports({ status: 'pending approver' })
    return data?.reports
  })

  const navigate = useNavigate()
  const location = useLocation()
  const handleNavigate = (reportID) => {
    navigate(`/app/claim/reports/${reportID}`, {
      state: { currentLocation: location },
    })
  }

  return (
    <Box>
      <Box
        sx={{
          bgcolor: 'alternate.light',
          borderRadius: '4.5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          p: 2,
          m: 2,
          gap: 2,
        }}
      >
        <Box display="flex" alignCenter="center" sx={{ gap: 2 }}>
          <Icon sx={{ fontSize: 40, color: 'alternate.main' }}>account_balance_wallet_rounded</Icon>
          <Box>
            <Typography variant="body1" fontWeight="bold" color="dark-text.main">
              Total pending
            </Typography>
            <Typography variant="body2" color="light-gray-text.main">
              Total amount from reports pending approval
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
          {data && (
            <>
              <Typography variant="body1" color="light-gray-text.main">
                {`(${data?.length} claim reports)`}
              </Typography>
              <Typography variant="h4" color="alternate.main" fontWeight="bold">
                {`RM ${parseFloat(
                  data?.map((item) => item.amount_payable).reduce((a, b) => a + b, 0)
                ).toFixed(2)}`}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      {!isLoading && data?.length === 0 ? (
        <AdxEmptyStateV2 icon={<Clear />} title="No awaiting approval reports" />
      ) : (
        <AdxSortTable
          stickyHeader
          height="calc(100vh - 347px)"
          column={unsubmittedReportColumn}
          data={data ? data : ['', '', '']}
          nav={
            isLoading ? (
              <Box display="flex" alignItems="center" height="50px">
                <Skeleton variant="text" width={100} />
              </Box>
            ) : (
              <Typography
                variant="body1"
                color="alternate.main"
                mr={1}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                View
              </Typography>
            )
          }
          onClick={(e) => handleNavigate(e.id)}
        />
      )}
    </Box>
  )
}

export default WaitingApprovalReportTable
